import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [ids, setIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setIds([]);
      return;
    }
    api.get("/favorites/ids")
      .then(({ data }) => { if (!cancelled) setIds(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setIds([]); });
    return () => { cancelled = true; };
  }, [user]);

  const isFavorite = useCallback((id) => ids.includes(id), [ids]);

  const toggleFavorite = useCallback(async (restaurantId) => {
    if (!user) {
      toast.error(lang === "ar" ? "سجّل دخولك لإضافة المفضلة" : "Sign in to save favorites");
      return;
    }
    const currentlyFav = ids.includes(restaurantId);
    // Optimistic update
    setIds((prev) => currentlyFav ? prev.filter((x) => x !== restaurantId) : [...prev, restaurantId]);
    try {
      if (currentlyFav) {
        await api.delete(`/favorites/${restaurantId}`);
        toast.success(lang === "ar" ? "تمت الإزالة من المفضلة" : "Removed from favorites");
      } else {
        await api.post(`/favorites/${restaurantId}`);
        toast.success(lang === "ar" ? "تمت الإضافة للمفضلة" : "Added to favorites");
      }
    } catch {
      // Revert on failure
      setIds((prev) => currentlyFav ? [...prev, restaurantId] : prev.filter((x) => x !== restaurantId));
      toast.error(lang === "ar" ? "تعذّر تحديث المفضلة" : "Could not update favorites");
    }
  }, [user, ids, lang]);

  return (
    <FavoritesContext.Provider value={{ ids, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be inside FavoritesProvider");
  return ctx;
};
